import { filterClasses } from "@swivl-mono/helpers";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useEffect, useRef, useState } from "react";

import { DataTableBody } from "./DataTable.Body";
import { DataTableFooter } from "./DataTable.Footer";
import { DataTableHeader } from "./DataTable.Header";
import { selectColumn } from "./DataTable.SelectColumn";

// extend react-table types to include updatedata function in meta
declare module "@tanstack/react-table" {
  interface TableMeta<TData extends unknown> {
    updateData: (rowIndex: number, columnId: string, value: any) => void;
  }
}

export enum DataTableVariant {
  PRIMARY = "primary",
  SECONDARY = "secondary"
}

export const dataTableVariantClassNames: Record<DataTableVariant, { wrapper: string; table?: string; header?: string; rowHover: string }> = {
  // TODO: devin - rename to primary and secondary?
  [DataTableVariant.PRIMARY]: {
    wrapper: "size-full bg-white",
    table: "size-full",
    header: "bg-white",
    rowHover: "hover:bg-orange-50"
  },
  [DataTableVariant.SECONDARY]: {
    wrapper: "w-full bg-white border border-grey-300 rounded-sm",
    header: "bg-grey-100",
    rowHover: "hover:bg-grey-50"
  }
};
export interface DataTableProps {
  areCellsEditable?: boolean;
  areRowsSelectable?: boolean;
  error?: string | undefined;
  hasCheckboxColumn?: boolean;
  hasFooter?: boolean;
  columns: ColumnDef<any, any>[];
  manualConfig?: {
    manualSorting?: boolean;
    manualPagination?: boolean;
    manualFiltering?: boolean;
    useCursorPagination?: boolean; // enable cursor-based pagination instead of offset
  };
  onDataChange?: (rowIndex: number, columnId: string, value: any) => void;
  paginationState?: {
    totalCount?: number;
    hasNext?: boolean;
    hasPrev?: boolean;
    onNext?: () => void;
    onPrev?: () => void;
  };
  initialSorting?: SortingState;
  columnFilterOptions?: ColumnFiltersState;
  loading?: boolean;
  onSortingChange?: (sorting: SortingState) => void;
  onPaginationChange?: (pagination: any) => void;
  onFilteringChange?: (filtering: any) => void;
  data: { [key: string]: any }[];
  handleSelectCell?: (selectedId?: any) => void;
  variant?: DataTableVariant;
  onSelectedIdsChange?: (ids: string[]) => void;
  selectedRowId?: string | null;
  /** Render footer outside the table so it stays pinned to the bottom while the table scrolls */
  pinFooter?: boolean;
  pageCount?: number;
  pagination?: { pageIndex: number; pageSize: number };
  totalCount?: number;
  sorting?: SortingState;
}

const DataTable = ({
  areCellsEditable = false,
  areRowsSelectable = true,
  columnFilterOptions = [],
  error,
  hasCheckboxColumn = false,
  hasFooter = true,
  loading = false,
  manualConfig = {
    manualSorting: false,
    manualPagination: false,
    manualFiltering: false,
    useCursorPagination: false
  },
  paginationState,
  initialSorting = [],
  onSortingChange,
  onPaginationChange,
  onFilteringChange,
  onDataChange,
  columns,
  data,
  handleSelectCell,
  variant = DataTableVariant.PRIMARY,
  onSelectedIdsChange,
  selectedRowId,
  pinFooter = false,
  pageCount,
  pagination: controlledPagination,
  sorting: controlledSorting,
  totalCount
}: DataTableProps) => {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [internalSorting, setInternalSorting] = useState<SortingState>(initialSorting);
  const [internalPagination, setInternalPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(columnFilterOptions);
  const [headerMinWidths, setHeaderMinWidths] = useState<Record<string, number>>({});

  const allColumns = [...(hasCheckboxColumn ? [selectColumn] : []), ...columns];
  const { manualSorting, manualPagination, manualFiltering, useCursorPagination } = manualConfig;

  // warn -- editable cells are enabled without ondatachange callback
  useEffect(() => {
    if (areCellsEditable && !onDataChange) {
      console.warn("datatable: ondatachange callback is not provided for editable cells.");
    }
  }, [areCellsEditable, onDataChange]);

  // Keep internal pagination in sync with controlled pagination
  useEffect(() => {
    if (controlledPagination) {
      setInternalPagination(controlledPagination);
    }
  }, [controlledPagination?.pageIndex, controlledPagination?.pageSize]);

  // Use controlled states if provided, otherwise use internal state
  const sorting = controlledSorting ?? internalSorting;
  const pagination = controlledPagination ?? internalPagination;

  // apply dynamic minimum widths based on header measurements
  const columnsWithMinWidths = allColumns.map((column) => {
    const headerMinWidth = headerMinWidths[column.id as string];
    const explicitMinSize = column.minSize;

    return {
      ...column,
      minSize: explicitMinSize || headerMinWidth || 60,
      maxSize: column.maxSize || 800
    };
  });

  const table = useReactTable({
    data,
    columns: columnsWithMinWidths,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 60,
      maxSize: 800
    },
    state: {
      rowSelection,
      sorting,
      pagination,
      columnFilters
    },
    meta: {
      updateData: (rowIndex: number, columnId: string, value: any) => {
        onDataChange?.(rowIndex, columnId, value);
      }
    },
    onSortingChange: (updater) => {
      setInternalSorting((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;

        if (manualSorting && onSortingChange) {
          onSortingChange(next);
        }

        return next;
      });
    },
    onPaginationChange: (updater) => {
      setInternalPagination((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;

        if (manualPagination && onPaginationChange) {
          onPaginationChange(next);
        }

        return next;
      });
    },
    onColumnFiltersChange: manualFiltering
      ? (updater) => {
          const next = typeof updater === "function" ? updater(columnFilters) : updater;
          onFilteringChange?.(next);
        }
      : setColumnFilters,
    manualSorting: manualSorting,
    manualPagination: manualPagination,
    manualFiltering: manualFiltering,
    pageCount: manualPagination && pageCount !== undefined ? pageCount : undefined,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    enableRowSelection: areRowsSelectable,
    onRowSelectionChange: setRowSelection
  });

  useEffect(() => {
    if (!onSelectedIdsChange) return;

    const ids = Object.keys(rowSelection).filter((id) => rowSelection[id]);

    onSelectedIdsChange(ids);
  }, [rowSelection]);

  const dataTotalCount = manualPagination ? (totalCount ?? paginationState?.totalCount) : table.getFilteredRowModel().rows.length;

  // calculate total width of all columns to prevent checkbox clipping
  const totalColumnsWidth = table.getAllColumns().reduce((total, column) => total + column.getSize(), 0);

  const showFooter = hasFooter;
  const cursorPaginationState = useCursorPagination ? paginationState : undefined;

  return (
    <div
      className={filterClasses(
        "flex size-full flex-col",
        pinFooter ? "overflow-hidden" : "overflow-auto",
        dataTableVariantClassNames[variant].wrapper
      )}
    >
      <div className={pinFooter ? "min-h-0 flex-1 overflow-auto overscroll-none" : "contents"}>
        <table className="w-full table-fixed border-collapse px-3" style={{ minWidth: `${totalColumnsWidth}px` }}>
          {/* table header */}
          <DataTableHeader table={table} variant={variant} onHeaderWidthsChange={setHeaderMinWidths} />

          {/* table body */}
          <DataTableBody
            areCellsEditable={areCellsEditable}
            areRowsSelectable={areRowsSelectable}
            handleSelectCell={handleSelectCell}
            error={error}
            table={table}
            loading={loading}
            selectedRowId={selectedRowId}
            variant={variant}
          />

          {/* table footer (inline) */}
          {showFooter && !pinFooter && <DataTableFooter table={table} totalDataCount={dataTotalCount} paginationState={cursorPaginationState} />}
        </table>
      </div>

      {/* pinned footer (outside table) */}
      {showFooter && pinFooter && <DataTableFooter table={table} totalDataCount={dataTotalCount} paginationState={cursorPaginationState} pinned />}
    </div>
  );
};

export { DataTable };
